// B05 slice 7: the login GATE.
//
// Not a route. `App` returns this instead of the shell when the auth store holds
// no live session, so `ROUTES` is untouched and the user lands back on whatever
// hash they were on. Deliberate: a `#/login` route would be a URL people bookmark
// and share, and there is nothing behind it.

import { useRef, useState } from 'react';
import * as auth from '../data/auth-store.js';

export function Login({ onSuccess }) {
  const [username, setUsername] = useState('root');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // React 18 StrictMode double-invokes effects and can deliver a doubled submit
  // in dev; a login costs the server ~50-100ms of BLAKE3 on the connection's own
  // main-loop thread, so a duplicate is not free. The ref (not state) is what
  // makes the guard synchronous — two submits in the same tick both see it.
  const inFlight = useRef(false);

  async function submit(e) {
    e?.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      await auth.login(username, password);
      // The password's life ends here: the store keeps only the token (see
      // auth-store.js) and the WebSocket re-auths with that. Clearing the FIELD
      // keeps the plaintext out of React state and out of the DOM.
      setPassword('');
      onSuccess?.();
    } catch (err) {
      // The server sends ONE message for a wrong password, an unknown user and a
      // malformed body alike, and equalizes the timing — so there is deliberately
      // nothing more specific to show, and guessing would be worse than quoting.
      setError(err?.message || 'login failed');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <img src="/kinedb-icon.svg" alt="" />
          <span>KineDB</span>
        </div>
        <p className="login-sub">Sign in to the management console.</p>

        <label className="login-field">
          <span>User</span>
          <input
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            spellCheck={false}
            disabled={busy}
          />
        </label>

        <label className="login-field">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={busy}
          />
        </label>

        {error && <div className="login-error" role="alert">{error}</div>}

        <button className="btn btn-primary login-submit" type="submit" disabled={busy || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        {/* Only the session TOKEN is persisted (auth-store.js): it survives a
            reload and a new tab for its 24 h lifetime; the password never
            leaves this form. A server restart / data reset invalidates every
            token and asks once more. */}
        <p className="login-note">
          You stay signed in for 24 hours or until you sign out; a server restart asks again.
        </p>
      </form>
    </div>
  );
}
